import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Users, UserPlus, FileText, ShoppingCart,
  AlertTriangle, Clock, Building2, LogIn, ChevronRight, CheckCircle2, Mail, Phone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KpiCard = ({ label, value, icon: Icon, accent = 'blue', testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-full bg-${accent}-100 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${accent}-600`} />
        </div>
      </div>
      <p className="text-3xl font-bold text-slate-800">{value?.toLocaleString('it-IT') || 0}</p>
    </CardContent>
  </Card>
);

const AlertBox = ({ title, description, items, icon: Icon, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className="w-4 h-4 text-amber-500" />
        {title}
        <Badge variant="outline" className="ml-auto text-[10px]">
          Fase 2
        </Badge>
      </CardTitle>
      <p className="text-xs text-slate-500 pl-6">{description}</p>
    </CardHeader>
    <CardContent>
      {items.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-sm">
          Nessuna anomalia rilevata
          <p className="text-[11px] text-slate-300 mt-1">Il monitoraggio verrà attivato in Fase 2</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-slate-400" />
              {it.label}
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

const ConvenzioneOverviewPage = () => {
  const { convenzioneId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, enterConvenzione, exitConvenzione, activeConvenzione } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // Fase 1.4: iscritti in attesa di approvazione
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvingIds, setApprovingIds] = useState(new Set());
  // Traccia se abbiamo già auto-attivato la convenzione per questo convenzioneId.
  // Evita che l'useEffect di auto-attivazione ripristini la convenzione dopo che
  // l'utente ha cliccato "Torna alla vista completa" (che azzera activeConvenzione).
  const autoActivatedRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/convenzioni/${convenzioneId}/overview`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/convenzioni/${convenzioneId}/pending-users`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setData(ovRes.data);
      setPendingUsers(pendingRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento dashboard convenzione');
      navigate('/dashboard/convenzioni');
    } finally {
      setLoading(false);
    }
  }, [convenzioneId, getAuthHeader, navigate]);

  const approveUser = async (userId, userName) => {
    setApprovingIds(prev => new Set(prev).add(userId));
    try {
      await axios.post(
        `${API}/admin/convenzioni/${convenzioneId}/users/${userId}/approve`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(`${userName} è stato attivato`, {
        description: "Email di conferma inviata.",
        duration: 3500,
      });
      // Rimuove dall'elenco locale e decrementa KPI
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      setData(prev => prev ? { ...prev, kpi: { ...prev.kpi, pending_count: Math.max(0, (prev.kpi.pending_count || 1) - 1) } } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'attivazione");
    } finally {
      setApprovingIds(prev => {
        const n = new Set(prev);
        n.delete(userId);
        return n;
      });
    }
  };

  useEffect(() => { load(); }, [load]);

  // Auto-attiva filtro convenzione UNA SOLA VOLTA per convenzioneId al primo caricamento
  // dei dati. Non si riattiva se l'utente esce esplicitamente (che azzera activeConvenzione).
  useEffect(() => {
    if (!data?.convenzione) return;
    if (autoActivatedRef.current === data.convenzione.id) return;
    autoActivatedRef.current = data.convenzione.id;
    if (activeConvenzione?.id !== data.convenzione.id) {
      enterConvenzione({
        id: data.convenzione.id,
        nome: data.convenzione.nome,
        codice_univoco: data.convenzione.codice_univoco,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.convenzione?.id]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (!data) return null;

  const { convenzione, kpi, alerts } = data;

  return (
    <div className="p-6 space-y-6" data-testid="convenzione-overview">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {convenzione.logo_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${convenzione.logo_url}`}
              alt={convenzione.nome}
              className="max-h-16 max-w-[120px] object-contain"
            />
          ) : (
            <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-8 h-8 text-emerald-600" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {convenzione.nome}
              {convenzione.is_default && <Badge className="bg-blue-100 text-blue-700 text-[10px]">DEFAULT</Badge>}
            </h1>
            <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
              <span>Codice: <span className="font-mono font-semibold text-slate-800">{convenzione.codice_univoco}</span></span>
              <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">IN GESTIONE</Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/convenzioni')} className="gap-1">
            <ArrowLeft className="w-4 h-4" /> Torna alla lista
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const nomeConv = convenzione?.nome;
              exitConvenzione();
              navigate('/dashboard', { replace: true });
              if (nomeConv) {
                toast.success(`Uscito da ${nomeConv}`, {
                  description: 'Torna alla vista completa Sede Centrale.',
                  duration: 3000,
                });
              }
            }}
            className="gap-1"
            data-testid="btn-exit-from-overview"
          >
            Esci dalla convenzione
          </Button>
        </div>
      </div>

      {/* KPI - 4 Card in alto */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Clienti Iscritti"
          value={kpi.clienti_totali}
          icon={Users}
          accent="blue"
          testid="kpi-clienti"
        />
        <KpiCard
          label="Nuovi (7 giorni)"
          value={kpi.nuovi_7gg}
          icon={UserPlus}
          accent="emerald"
          testid="kpi-nuovi-7gg"
        />
        <KpiCard
          label="Offerte Generate"
          value={kpi.offerte_totali}
          icon={FileText}
          accent="amber"
          testid="kpi-offerte"
        />
        <KpiCard
          label="Ordini Inseriti"
          value={kpi.ordini_totali}
          icon={ShoppingCart}
          accent="violet"
          testid="kpi-ordini"
        />
      </div>

      {/* Alerts placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AlertBox
          title="Clienti in Blocco"
          description="Utenti che hanno superato i 3 tentativi di ricerca nel funnel"
          items={alerts.clienti_in_blocco}
          icon={AlertTriangle}
          testid="alert-clienti-blocco"
        />
        <AlertBox
          title="Lead Inattivi"
          description="Utenti che non compiono azioni o non rispondono da oltre 72 ore"
          items={alerts.lead_inattivi}
          icon={Clock}
          testid="alert-lead-inattivi"
        />
      </div>

      {/* Iscritti in attesa di approvazione (Fase 1.4) */}
      <Card data-testid="pending-users-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Iscritti in Attesa di Approvazione</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  I nuovi clienti attivano l'account solo dopo la tua conferma.
                </p>
              </div>
            </div>
            {pendingUsers.length > 0 && (
              <Badge className="bg-amber-500 text-white text-sm px-3" data-testid="pending-count-badge">
                {pendingUsers.length}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {pendingUsers.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              Nessuna iscrizione in attesa. Tutto a posto.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pendingUsers.map(u => (
                <div key={u.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`pending-user-${u.id}`}>
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-sm">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-medium text-slate-800 text-sm truncate">{u.name || u.email}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span>
                      {u.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {u.telefono}</span>}
                      {u.created_at && (
                        <span className="text-slate-400">
                          {new Date(u.created_at).toLocaleDateString('it-IT')}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                    disabled={approvingIds.has(u.id)}
                    onClick={() => approveUser(u.id, u.name || u.email)}
                    data-testid={`btn-approve-${u.id}`}
                  >
                    {approvingIds.has(u.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Autorizza
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shortcut navigation */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase font-semibold mb-3">Azioni Rapide</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button variant="outline" onClick={() => navigate('/dashboard/rubrica')} className="gap-1 justify-start" data-testid="link-rubrica">
              <LogIn className="w-4 h-4" /> Vai alla Rubrica
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/offerte')} className="gap-1 justify-start" data-testid="link-offerte">
              <LogIn className="w-4 h-4" /> Vai alle Offerte
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/ordini')} className="gap-1 justify-start" data-testid="link-ordini">
              <LogIn className="w-4 h-4" /> Vai agli Ordini
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConvenzioneOverviewPage;
